/**
 * Hook y store de tema (light/dark/system) con persistencia en localStorage
 * y sincronización con prefers-color-scheme cuando theme es 'system'.
 */

import { create } from 'zustand';

const THEME_KEY = 'migro-theme';

export type Theme = 'light' | 'dark' | 'system';

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const s = localStorage.getItem(THEME_KEY);
  if (s === 'dark' || s === 'system') return s;
  return 'light';
}

function getEffectiveTheme(theme: Theme): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

function applyToDocument(effective: 'light' | 'dark') {
  document.documentElement.classList.toggle('dark', effective === 'dark');
}

let mediaQuery: MediaQueryList | null = null;

function cleanupSystemListener() {
  if (mediaQuery) {
    mediaQuery.removeEventListener('change', systemListener);
    mediaQuery = null;
  }
}

function systemListener() {
  const effective = getEffectiveTheme('system');
  applyToDocument(effective);
  useThemeStore.setState({ resolvedTheme: effective });
}

function setupSystemListener() {
  if (typeof window === 'undefined') return;
  cleanupSystemListener();
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', systemListener);
}

interface ThemeStore {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  resolvedTheme: 'light' | 'dark';
}

export const useThemeStore = create<ThemeStore>((set, get) => {
  const theme = getStoredTheme();
  const effective = getEffectiveTheme(theme);
  applyToDocument(effective);
  if (theme === 'system') {
    setupSystemListener();
  }

  return {
    theme,
    resolvedTheme: effective,
    setTheme: (t: Theme) => {
      localStorage.setItem(THEME_KEY, t);
      const effectiveNew = getEffectiveTheme(t);
      applyToDocument(effectiveNew);
      if (t === 'system') {
        setupSystemListener();
      } else {
        cleanupSystemListener();
      }
      set({ theme: t, resolvedTheme: effectiveNew });
    },
    toggleTheme: () => {
      const current = get().theme;
      const next: Theme = current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
      get().setTheme(next);
    },
  };
});

/**
 * Hook para usar el tema en componentes.
 * resolvedTheme refleja el tema efectivo (light/dark) para UI.
 */
export function useTheme() {
  const theme = useThemeStore((s) => s.theme);
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  return { theme, resolvedTheme, setTheme, toggleTheme };
}
